/**
 * Compresses an image file to a target maximum size using Canvas API.
 * Resizes dimensions and adjusts JPEG quality to achieve target size.
 */

const MAX_DIMENSION = 1920;
const INITIAL_QUALITY = 0.85;
const MIN_QUALITY = 0.3;
const QUALITY_STEP = 0.1;

/**
 * Loads a File as an HTMLImageElement
 */
const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Calculates new dimensions maintaining aspect ratio
 */
const calculateDimensions = (
  width: number,
  height: number,
  maxDimension: number,
): { width: number; height: number } => {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height };
  }

  const ratio = Math.min(maxDimension / width, maxDimension / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
};

/**
 * Converts canvas to Blob with specified quality
 */
const canvasToBlob = (
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create blob"));
        }
      },
      "image/jpeg",
      quality,
    );
  });
};

/**
 * Compresses an image file to be under the target size.
 * @param file - The image file to compress
 * @param maxSizeBytes - Maximum file size in bytes (default 2MB)
 * @returns Compressed file, or original if already under target size
 */
export const compressImage = async (
  file: File,
  maxSizeBytes: number = 2 * 1024 * 1024,
): Promise<File> => {
  // If file is already under target size, return as-is
  if (file.size <= maxSizeBytes) {
    return file;
  }

  const img = await loadImage(file);
  const { width, height } = calculateDimensions(
    img.width,
    img.height,
    MAX_DIMENSION,
  );

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  // Use high-quality image scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, width, height);

  // Try progressively lower quality until under target size
  let quality = INITIAL_QUALITY;
  let blob = await canvasToBlob(canvas, quality);

  while (blob.size > maxSizeBytes && quality > MIN_QUALITY) {
    quality -= QUALITY_STEP;
    blob = await canvasToBlob(canvas, quality);
  }

  // Generate filename with .jpg extension
  const originalName = file.name.replace(/\.[^/.]+$/, "");
  const compressedFileName = `${originalName}.jpg`;

  return new File([blob], compressedFileName, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};
